import { AlertModal, Button, Checkbox, Modal, TextBlock } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import style from "./file-sharing.scss";
import { computePath, isValidFolderPath } from "./fileTreeUtils";
import FolderPathSelector from "./FolderPathSelector";
import { useAbortControllers } from "./useAbortControllers";
import { hasHttpStatus, ROOT_PATH, SharedFile } from "domain/fileSharing";
import { fileSharingService } from "services/file-sharing/FileSharingService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface Props {
    item: SharedFile;
    existingPaths: string[];
    isOpen: boolean;
    onClose: () => void;
    onMoved: () => void;
}

const MoveFileModal = ({ item, existingPaths, isOpen, onClose, onMoved }: Props): JSX.Element => {
    const { t } = useTranslation();
    const abortControllers = useAbortControllers();

    const [selectedPath, setSelectedPath] = React.useState("");
    const [newFolder, setNewFolder] = React.useState("");
    const [moveAllowOverride, setMoveAllowOverride] = React.useState(false);
    const [moveLoading, setMoveLoading] = React.useState(false);
    const [moveError, setMoveError] = React.useState<string | undefined>();
    const [confirmOpen, setConfirmOpen] = React.useState(false);

    React.useEffect(() => {
        if (isOpen) {
            setSelectedPath(existingPaths.includes(item.directoryPath) ? item.directoryPath : "");
            setNewFolder("");
            setMoveAllowOverride(false);
            setMoveError(undefined);
            setConfirmOpen(false);
        }
    }, [isOpen, item.directoryPath, existingPaths]);

    const handleMove = React.useCallback(() => {
        usageStatisticsService.sendEvent({ category: Category.FILE_SHARING, action: Action.MOVE_FILE });
        const abortController = new AbortController();
        abortControllers.push(abortController);
        setMoveLoading(true);
        setMoveError(undefined);
        fileSharingService
            .moveFile(
                {
                    uuid: item.uuid,
                    new_path: computePath(selectedPath, newFolder) || ROOT_PATH,
                    allow_override: moveAllowOverride,
                },
                abortController
            )
            .then(() => {
                setConfirmOpen(false);
                onMoved();
                onClose();
            })
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to move shared file", error);
                    const status = hasHttpStatus(error) ? error.status : undefined;
                    setMoveError(
                        status === 400
                            ? t("FileSharing.move.errorConflict", {
                                  allowOverride: t("FileSharing.move.allowOverride"),
                              })
                            : status === 404
                              ? t("FileSharing.move.errorNotFound")
                              : t("Common.resultErrorMessage")
                    );
                    setConfirmOpen(false);
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setMoveLoading(false);
                }
            });
    }, [item.uuid, selectedPath, newFolder, moveAllowOverride, onMoved, onClose, t]);

    if (confirmOpen) {
        return (
            <AlertModal
                title={t("FileSharing.move.confirmTitle", { name: item.filename })}
                variant="Warning"
                isOpen={true}
                hideModal={() => !moveLoading && setConfirmOpen(false)}
                className={style.dialog}
                buttons={
                    <>
                        <Button
                            variant="Secondary"
                            onClick={() => setConfirmOpen(false)}
                            disabled={moveLoading}
                            testId={testIds.common.dialog.closeButton}
                        >
                            {t("Common.cancel")}
                        </Button>
                        <Button
                            variant="Primary"
                            isLoading={moveLoading}
                            onClick={handleMove}
                            testId={testIds.common.confirmationDialog.confirmButton}
                        >
                            {t("FileSharing.move.move")}
                        </Button>
                    </>
                }
            >
                <TextBlock>
                    {t("FileSharing.move.confirmBody", {
                        from: item.directoryPath || ROOT_PATH,
                        to: computePath(selectedPath, newFolder) || ROOT_PATH,
                    })}
                </TextBlock>
            </AlertModal>
        );
    }

    return (
        <Modal
            isOpen={isOpen}
            hideModal={onClose}
            title={t("FileSharing.move.title")}
            className={style.dialog}
            buttons={
                <>
                    <Button variant="Secondary" onClick={onClose} testId={testIds.common.dialog.closeButton}>
                        {t("Common.cancel")}
                    </Button>
                    <Button
                        variant="Primary"
                        disabled={moveLoading}
                        onClick={() => {
                            if (!isValidFolderPath(newFolder)) {
                                setMoveError(t("FileSharing.move.errorInvalidFolderName"));
                                return;
                            }
                            setConfirmOpen(true);
                        }}
                        testId={testIds.common.dialog.primaryButton}
                    >
                        {t("FileSharing.move.move")}
                    </Button>
                </>
            }
        >
            <div className={style.locationSection}>
                <FolderPathSelector
                    existingPaths={existingPaths}
                    selectedPath={selectedPath}
                    newFolder={newFolder}
                    pathLabel={t("FileSharing.move.pathLabel")}
                    newFolderLabel={t("FileSharing.move.newFolderLabel")}
                    newFolderPlaceholder={t("FileSharing.move.pathPlaceholder")}
                    pathSelectId="move-path-select"
                    newFolderInputId="move-new-folder"
                    newFolderInputTestId={testIds.workArea.fileSharing.moveDialog.folderSelect}
                    onSelectedPathChange={(path) => {
                        setSelectedPath(path);
                        setMoveError(undefined);
                    }}
                    onNewFolderChange={(folder) => {
                        setNewFolder(folder);
                        setMoveError(undefined);
                    }}
                />
            </div>
            <Checkbox
                label={t("FileSharing.move.allowOverride")}
                name="move-allow-override"
                checked={moveAllowOverride}
                hideLabelSection={true}
                onChange={(e) => setMoveAllowOverride(e.target.checked)}
            />
            {moveError && <div className={style.errorMessage}>{moveError}</div>}
        </Modal>
    );
};

export default MoveFileModal;
